import { useCallback, useMemo, useState } from 'react';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import { SubscriptionSummary } from '../../../../types/subscription';
import { Exchange } from 'grts-utils/types/Exchange';
import { useDepositDistributionState } from './hooks/useDepositDistributionState';
import DepositConfirmationModal from './ConfirmationModal';
import DropdownComponent from '../../../../components/Form/Dropdown';
import { Dropdown } from '../../../../components/Form';
import AssetIcon from '../../../../components/AssetIcon';
import { colors } from '../../../../colors';
import { formatNumber } from '../../../../helpers/NumberFormatter';
import './index.css';

type AvailableBalances = Record<string, Record<string, number>>;

interface Props {
  subscriptions: SubscriptionSummary[];
  exchanges: Exchange[];
  selectedExchange: Exchange | null;
  onExchangeChange: (exchange: Exchange) => void;
  availableBalances: AvailableBalances | undefined;
}

function roundDisplay(val: number, decimals: number): string {
  return Number(val.toFixed(decimals)).toString();
}

const STABLE_ASSETS = ['USDC', 'USDT', 'USD', 'EUR'];

const DepositDistribution = ({
  subscriptions,
  exchanges,
  selectedExchange,
  onExchangeChange,
  availableBalances,
}: Props) => {
  const availableByQuoteAsset = useMemo<Record<string, number>>(() => {
    if (!availableBalances || !selectedExchange) return {};
    return availableBalances[selectedExchange] ?? {};
  }, [availableBalances, selectedExchange]);

  const {
    entries,
    allocatedByQuoteAsset,
    hasChanges,
    changedEntries,
    setDepositAmount,
    setDepositPercentage,
    toggleLock,
    reset,
  } = useDepositDistributionState(
    subscriptions,
    selectedExchange,
    availableByQuoteAsset,
  );

  const [showConfirmation, setShowConfirmation] = useState(false);

  const exchangeDropdownConfig: Dropdown = useMemo(
    () => ({
      label: 'Exchange',
      options: exchanges,
      value: selectedExchange || undefined,
      handleValueChange: (val: string | number) =>
        onExchangeChange(val as Exchange),
      shouldFit: true,
    }),
    [exchanges, selectedExchange, onExchangeChange],
  );

  const remainingByQuoteAsset = useMemo(() => {
    const result: Record<string, number> = {};
    for (const [qa, available] of Object.entries(availableByQuoteAsset)) {
      result[qa] = available - (allocatedByQuoteAsset[qa] ?? 0);
    }
    return result;
  }, [availableByQuoteAsset, allocatedByQuoteAsset]);

  const visibleStableAssets = useMemo(
    () =>
      Object.entries(availableByQuoteAsset).filter(
        ([qa, amount]) => STABLE_ASSETS.includes(qa) && amount > 0,
      ),
    [availableByQuoteAsset],
  );

  if (!subscriptions.length) {
    return (
      <div className="deposit-distribution">
        <p className="deposit-distribution-empty">
          No subscriptions found. Select a cluster with subscriptions to begin.
        </p>
      </div>
    );
  }

  return (
    <div className="deposit-distribution">
      <div className="deposit-distribution-header">
        <div className="deposit-distribution-title-row">
          <h2 className="deposit-distribution-title">Deposit Distribution</h2>
          {exchanges.length > 1 && (
            <div className="deposit-distribution-exchange-dropdown">
              <DropdownComponent config={exchangeDropdownConfig} />
            </div>
          )}
        </div>
        <div className="deposit-distribution-actions">
          <button
            className="deposit-distribution-btn reset"
            onClick={reset}
            disabled={!hasChanges}
          >
            Reset
          </button>
          <button
            className="deposit-distribution-btn submit"
            onClick={() => setShowConfirmation(true)}
            disabled={!hasChanges}
          >
            Submit Deposit
          </button>
        </div>
      </div>

      <div className="deposit-available-section">
        <span className="deposit-available-label">
          Available on {selectedExchange}:
        </span>
        <div className="deposit-available-values">
          {visibleStableAssets.length > 0 ? (
            visibleStableAssets.map(([qa]) => (
              <span key={qa} className="deposit-available-value">
                {formatNumber(remainingByQuoteAsset[qa] ?? 0, 2)} {qa}
              </span>
            ))
          ) : (
            <span className="deposit-available-value deposit-available-none">
              —
            </span>
          )}
        </div>
      </div>

      <div className="deposit-cards-container">
        {entries.map((entry) => (
          <DepositCard
            key={entry.subscriptionId}
            entry={entry}
            onAmountChange={setDepositAmount}
            onPercentageChange={setDepositPercentage}
            onToggleLock={toggleLock}
          />
        ))}
      </div>

      {showConfirmation && (
        <DepositConfirmationModal
          changedEntries={changedEntries}
          onClose={() => setShowConfirmation(false)}
        />
      )}
    </div>
  );
};

interface DepositCardProps {
  entry: {
    subscriptionId: number;
    botFriendlyName: string;
    baseAsset: string;
    quoteAsset: string;
    exchange: Exchange;
    currentStableBalance: number;
    currentTotalBalance: number;
    depositAmount: number;
    depositPercentage: number;
    isLocked: boolean;
  };
  onAmountChange: (subscriptionId: number, amount: number) => void;
  onPercentageChange: (subscriptionId: number, pct: number) => void;
  onToggleLock: (subscriptionId: number) => void;
}

const DepositCard = ({
  entry,
  onAmountChange,
  onPercentageChange,
  onToggleLock,
}: DepositCardProps) => {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleFocus = useCallback((field: string, currentValue: string) => {
    setFocusedField(field);
    setEditValue(currentValue);
  }, []);

  const handleBlur = useCallback(
    (field: string) => {
      const val = parseFloat(editValue);
      if (!isNaN(val) && val >= 0) {
        if (field === 'amount') onAmountChange(entry.subscriptionId, val);
        if (field === 'pct')
          onPercentageChange(entry.subscriptionId, Math.min(100, val));
      }
      setFocusedField(null);
    },
    [editValue, entry.subscriptionId, onAmountChange, onPercentageChange],
  );

  const cardClass = `deposit-card ${entry.isLocked ? 'locked' : 'unlocked'}`;

  return (
    <div className={cardClass}>
      <div className="deposit-card-header">
        <div className="deposit-card-name-row">
          <AssetIcon
            baseAsset={entry.baseAsset}
            width={24}
            height={24}
            style={{ marginBottom: 0, borderRadius: 100 }}
          />
          <span className="deposit-card-name">{entry.botFriendlyName}</span>
        </div>
        <button
          className="deposit-card-lock"
          onClick={() => onToggleLock(entry.subscriptionId)}
        >
          {entry.isLocked ? (
            <FaLock color={colors.orange} size={16} />
          ) : (
            <FaLockOpen color={colors.green} size={16} />
          )}
        </button>
      </div>

      <div className="deposit-card-balance">
        <span className="deposit-card-balance-label">
          Total ({entry.quoteAsset})
        </span>
        <span className="deposit-card-balance-value">
          {formatNumber(entry.currentTotalBalance, 2)}
        </span>
      </div>

      <div className="deposit-card-inputs">
        <div className="deposit-card-input-group">
          <label>Deposit ({entry.quoteAsset})</label>
          <input
            type="text"
            value={
              focusedField === 'amount'
                ? editValue
                : roundDisplay(entry.depositAmount, 2)
            }
            onFocus={() =>
              handleFocus('amount', roundDisplay(entry.depositAmount, 2))
            }
            onBlur={() => handleBlur('amount')}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleBlur('amount')}
            disabled={entry.isLocked}
            style={{
              color: entry.depositAmount > 0 ? colors.green : colors.white,
            }}
          />
        </div>
        <div className="deposit-card-input-group">
          <label>Pct (%)</label>
          <input
            type="text"
            value={
              focusedField === 'pct'
                ? editValue
                : roundDisplay(entry.depositPercentage, 1)
            }
            onFocus={() =>
              handleFocus('pct', roundDisplay(entry.depositPercentage, 1))
            }
            onBlur={() => handleBlur('pct')}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleBlur('pct')}
            disabled={entry.isLocked}
            style={{
              color: entry.depositPercentage > 0 ? colors.green : colors.white,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DepositDistribution;
